/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import libraries */
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';

/* Import types */
import type { DeviceSensorCsvMetadataGroup } from '@/types/deviceSensor';

export type DemoSensorDeviceAvailability = 'enabled' | 'all';
export type DemoSensorDeviceGroupBy = 'system' | 'anomaly';

export interface DemoSensorDeviceOptionGroup {
    devices: string[];
    label: string;
}

export const DEMO_SENSOR_SUPPLY_TYPE_OPTIONS: Array<{
    label: string;
    value: DeviceSensorCsvMetadataGroup;
}> = [
    { label: 'Photovoltaics', value: 'PV' },
    { label: 'Electricity', value: 'BL' },
    { label: 'Heat', value: 'HE' },
    { label: 'Gas', value: 'GAS' },
    { label: 'Grid Feed', value: 'GRID_FEED' },
    { label: 'Water', value: 'WATER' },
];

/** Groups the available sensor/device choices without changing their membership or order. */
export function getDemoSensorDeviceOptionGroups(
    deviceSensorCatalog: DeviceSensorCatalog,
    virtualDevices: string[],
    groupBy: DemoSensorDeviceGroupBy,
    translate: (key: string) => string,
): DemoSensorDeviceOptionGroup[] {
    if (groupBy === 'system') {
        const devicesBySystem = new Map<string, string[]>();

        virtualDevices.forEach((virtualDevice) => {
            const system = deviceSensorCatalog.getDeviceSystem(virtualDevice) ?? virtualDevice;
            const devices = devicesBySystem.get(system) ?? [];

            devices.push(virtualDevice);
            devicesBySystem.set(system, devices);
        });

        return Array.from(devicesBySystem.entries())
            .map(([system, devices]) => ({
                devices: devices.sort((left, right) => left.localeCompare(right)),
                label: system,
            }))
            .sort((left, right) => left.label.localeCompare(right.label));
    }

    const devicesByAnomaly = new Map<DeviceSensorCsvMetadataGroup, string[]>();
    const devicesWithoutAnomaly: string[] = [];

    virtualDevices.forEach((virtualDevice) => {
        const metadataGroups = deviceSensorCatalog.getDeviceMetadataGroups(virtualDevice);
        let hasKnownAnomaly = false;

        DEMO_SENSOR_SUPPLY_TYPE_OPTIONS.forEach(({ value }) => {
            if (!metadataGroups.includes(value)) {
                return;
            }

            hasKnownAnomaly = true;
            const devices = devicesByAnomaly.get(value) ?? [];

            devices.push(virtualDevice);
            devicesByAnomaly.set(value, devices);
        });

        if (!hasKnownAnomaly) {
            devicesWithoutAnomaly.push(virtualDevice);
        }
    });

    const anomalyGroups = DEMO_SENSOR_SUPPLY_TYPE_OPTIONS
        .map(({ label, value }) => ({
            devices: devicesByAnomaly.get(value) ?? [],
            label: translate(label),
        }))
        .filter(({ devices }) => devices.length > 0)
        .map(({ devices, label }) => ({
            devices: devices.sort((left, right) => left.localeCompare(right)),
            label,
        }));

    if (devicesWithoutAnomaly.length > 0) {
        anomalyGroups.push({
            devices: devicesWithoutAnomaly.sort((left, right) => left.localeCompare(right)),
            label: translate('Unknown'),
        });
    }

    return anomalyGroups;
}
